<template>
    <div class="row">
        <h4 class="col-12 mt-3">
            {{ $t('shop.selected_country_license') }}
        </h4>
        <div class="col-12">
            <div class="form-group w-100">
                <label class="col-form-label" for="country_code">
                    {{ $t('shop.licensing_state') }}
                </label>
                <select
                    name="country_code"
                    id="country_code"
                    class="form-control height-auto-override"
                    v-model="selectedState"
                    required
                >
                    <option disabled value="false" selected>
                        {{ $t('shop.dropdown_required') }}
                    </option>
                    <option
                        v-for="state in states"
                        :key="state.country.code"
                        :value="state.country.code"
                        :data-name="state.country.name"
                    >
                        {{ $t('country.' + state.country.code) }}
                    </option>
                </select>
            </div>
        </div>
        <div class="col-12 col-md-6">
            <div class="form-group">
                <label class="col-form-label" for="license_input">
                    {{ $t('shop.license_plate') }}
                </label>
                <license-plate-input
                    id="licensePlate"
                    name="license"
                    :is-invalid="typeof formErrors.license === 'object' && formErrors.license.length > 0"
                    @input="parseLicensePlateInput(false)"
                    :license-plate-country="licenseInputTemplate[0]"
                    :template="licenseInputTemplate[0].template"
                    :required="true"
                    v-model="licensePlate"
                    @license-plate-check-status="handleLicensePlateCheckEmitStatus"
                >
                    <span class="d-block" v-for="error in formErrors.license" :key="error">{{ error }}</span>
                </license-plate-input>
            </div>
            <div v-if="licensePlateShowError" class="ml-3 text-danger">
                {{ $t('shop.licensePlateLiveValidationError') }}
            </div>
        </div>
        <div class="col-12 col-md-6">
            <div class="form-group">
                <label class="col-form-label" for="license_input_confirm">
                    {{ $t('shop.license_plate_confirm') }}
                </label>
                <license-plate-input
                    :disable-validation="true"
                    id="licenseConfirm"
                    name="license_confirmation"
                    :is-invalid="
                        typeof formErrors.license_confirmation === 'object' &&
                        formErrors.license_confirmation.length > 0
                    "
                    @input="parseLicensePlateInput(true)"
                    :license-plate-country="licenseInputTemplate[0]"
                    :template="licenseInputTemplate[0].template"
                    :required="true"
                    v-model="licensePlateConfirm"
                >
                    <span class="d-block" v-for="error in formErrors.license_confirmation" :key="error">{{
                        error
                    }}</span>
                </license-plate-input>
            </div>
        </div>
        <div class="col-12" v-if="chassisNumberInputIsRequired">
            <div class="form-group w-100">
                <label for="chassis_number">
                    {{ $t('shop.chassis_number') }}
                </label>
                <input
                    type="text"
                    name="chassis_number"
                    class="form-control"
                    id="chassis_number"
                    v-model="chassisNumber"
                    maxlength="17"
                    :placeholder="$t('shop.chassis_number_placeholder')"
                    :class="{ 'is-invalid': showChassisNumberError }"
                    required
                />
                <div class="invalid-feedback" v-if="showChassisNumberError">
                    <span class="d-block" v-html="$t('shop.chassis_number_error')"></span>
                </div>
            </div>
        </div>
        <h4 class="col-12 mt-3">
            {{ $t('shop.selected_contact_details') }}
        </h4>
        <div class="col-12 col-md-6">
            <div class="form-group">
                <label for="first_name">
                    {{ $t('shop.paypal_first_name') }}
                </label>
                <input
                    type="text"
                    name="paypal_first_name"
                    class="form-control"
                    id="payPalFirstName"
                    v-model="firstName"
                    :placeholder="$t('shop.first_name_placeholder')"
                    required
                />
                <div class="invalid-feedback">
                    <span class="d-block" v-for="error in formErrors.paypal_first_name" :key="error">{{ error }}</span>
                </div>
            </div>
        </div>
        <div class="col-12 col-md-6">
            <div class="form-group">
                <label for="last_name">
                    {{ $t('shop.paypal_last_name') }}
                </label>
                <input
                    type="text"
                    name="paypal_last_name"
                    class="form-control"
                    id="payPalLastName"
                    v-model="lastName"
                    :placeholder="$t('shop.last_name_placeholder')"
                    required
                />
                <div class="invalid-feedback">
                    <span class="d-block" v-for="error in formErrors.paypal_last_name" :key="error">{{ error }}</span>
                </div>
            </div>
        </div>
        <div class="col-12">
            <div class="form-group">
                <label for="email">
                    {{ $t('shop.email') }}
                </label>
                <input
                    type="email"
                    name="email"
                    class="form-control"
                    id="email"
                    :placeholder="$t('shop.email_placeholder')"
                    v-model="email"
                    required
                />
                <div class="invalid-feedback">
                    <span class="d-block" v-for="error in formErrors.email" :key="error">{{ error }}</span>
                </div>
            </div>
        </div>
        <div class="col-12 mb-2">
            <div class="row custom-control-switch-row">
                <div v-if="this.expiryOfRightOfWithdrawalActive" class="col-12">
                    <div class="custom-control custom-switch">
                        <div class="form-group mb-1">
                            <input
                                class="custom-control-input"
                                name="residence_in_austria"
                                type="checkbox"
                                value="1"
                                id="residence_in_austria"
                                v-model="residenceInAustria"
                            />
                            <label
                                class="custom-control-label font-weight-normal"
                                for="residence_in_austria"
                                v-html="$t('shop.residenceInAustria')"
                            >
                            </label>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <div class="custom-control custom-switch">
                        <div class="form-group">
                            <input
                                class="custom-control-input"
                                name="invoice_wanted"
                                type="checkbox"
                                value="1"
                                id="invoice_wanted"
                                v-model="invoiceWanted"
                            />
                            <label
                                class="custom-control-label font-weight-normal"
                                for="invoice_wanted"
                                v-html="$t('shop.invoice_wanted')"
                            ></label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <template v-if="invoiceWanted">
            <div class="col-12 col-md-6">
                <div class="form-group">
                    <label for="invoice_first_name">
                        {{ $t('shop.first_name') }}
                    </label>
                    <input
                        type="text"
                        name="first_name"
                        class="form-control"
                        id="invoiceFirstName"
                        :placeholder="$t('shop.first_name_placeholder')"
                        required
                        v-model="invoiceFirstName"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.first_name" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-6">
                <div class="form-group">
                    <label for="invoice_last_name">
                        {{ $t('shop.last_name') }}
                    </label>
                    <input
                        type="text"
                        name="last_name"
                        class="form-control"
                        id="invoiceLastName"
                        :placeholder="$t('shop.last_name_placeholder')"
                        required
                        v-model="invoiceLastName"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.last_name" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12">
                <div class="form-group">
                    <label for="company">
                        {{ $t('shop.company') }}
                    </label>
                    <input
                        type="text"
                        name="company"
                        class="form-control"
                        id="company"
                        :placeholder="$t('shop.company_placeholder')"
                        required
                        v-model="company"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.company" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-8">
                <div class="form-group">
                    <label for="street">
                        {{ $t('shop.street') }}
                    </label>
                    <input
                        type="text"
                        name="street"
                        class="form-control"
                        id="street"
                        :placeholder="$t('shop.street_placeholder')"
                        required
                        v-model="street"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.street" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-4">
                <div class="form-group">
                    <label for="house">
                        {{ $t('shop.house_number') }}
                    </label>
                    <input
                        type="text"
                        name="house"
                        class="form-control"
                        id="house"
                        :placeholder="$t('shop.house_number_placeholder')"
                        required
                        v-model="house"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.house" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-4">
                <div class="form-group">
                    <label for="postcode">
                        {{ $t('shop.postcode') }}
                    </label>
                    <input
                        type="text"
                        name="postcode"
                        class="form-control"
                        id="postcode"
                        :placeholder="$t('shop.postcode_placeholder')"
                        required
                        v-model="postcode"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.postcode" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-8">
                <div class="form-group">
                    <label for="city">
                        {{ $t('shop.city') }}
                    </label>
                    <input
                        type="text"
                        name="city"
                        class="form-control"
                        id="city"
                        :placeholder="$t('shop.city_placeholder')"
                        required
                        v-model="city"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.city" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="col-12">
                <div class="form-group">
                    <label for="country">
                        {{ $t('shop.country') }}
                    </label>
                    <select name="country" id="country" class="form-control" required v-model="country">
                        <option disabled value="false" selected>
                            {{ $t('shop.dropdown_required') }}
                        </option>
                        <option
                            v-for="templateOption in inputTemplates"
                            :key="templateOption.country_name"
                            :value="templateOption.country_name"
                        >
                            {{ $t('country.' + templateOption.country_code) }}
                        </option>
                    </select>
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.country" :key="error">{{ error }}</span>
                    </div>
                </div>
            </div>
        </template>

        <ProductSummary
            :license-plate="userData?.license_plate"
            :selected-products="selectedProducts"
            @voucher-mounted="onVoucherMounted"
        ></ProductSummary>

        <OrderFormControls
            :user-data="userData"
            :product-type="productType"
            :providers-popup-enabled="providersPopupEnabled"
            :selected-products="selectedProducts"
            :voucher-code="voucherCode"
            @validation-failed="onValidationFailed"
            @submit-form="onFormSubmit"
            @chassis-number-error="showChassisNumberError = true"
            v-bind="$attrs"
        />
    </div>
</template>

<style lang="scss" scoped>
.invalid-feedback:not(:empty) {
    display: block;
}
</style>

<script>
import ProductSummary from './ProductSummary.vue';
import OrderFormControls from './OrderFormControls.vue';

export default {
    name: 'UserInput',

    components: {
        OrderFormControls,
        ProductSummary,
    },

    props: [
        'states',
        'inputTemplates',
        'selectedProducts',
        'sessionVoucher',
        'productType',
        'providersPopupEnabled',
        'enableExpiryOfRightOfWithdrawalSetting',
    ],

    data() {
        return {
            formErrors: {},
            selectedState: 'DE',
            licensePlate: '',
            licensePlateConfirm: '',
            email: null,
            invoiceWanted: false,
            invoiceFirstName: '',
            invoiceLastName: '',
            company: '',
            street: '',
            house: '',
            postcode: '',
            city: '',
            firstName: '',
            lastName: '',
            country: 'Germany',
            voucher: null,
            tosAccepted: false,
            receiveNewsletter: false,
            residenceInAustria: false,
            customerOutsideAustria: false,
            licensePlateCheckStatus: false,
            licensePlateShowError: false,
            timeoutLicensePlateInvalidId: 0,
            showChassisNumberError: false,
            chassisNumber: null,
            voucherCode: null,
        };
    },

    methods: {
        getSelectedLicensePlate() {
            return this.selectedState ? this.states.filter((x) => x.country.code === this.selectedState) : [];
        },

        onValidationFailed(errors) {
            this.formErrors = errors;

            // Scroll to first error.
            this.$nextTick(() => {
                const input = document.querySelector('.invalid-feedback:not(:empty)');
                if (input !== null) {
                    const elementY = input.parentElement.getBoundingClientRect().top;
                    const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                    const windowY = window.scrollY;

                    window.scrollTo(0, windowY + elementY - topNavHeight);
                }
            });
        },

        onFormSubmit() {
            this.licensePlateShowError = false;
            this.showChassisNumberError = false;
            this.$emit('submit-form');
        },

        onVoucherMounted(voucher) {
            this.voucherCode = voucher?.code ?? null;
        },

        getSelectedLicensePlateOption() {
            // has selected country and templates
            if (
                this.selectedState.length > 0 &&
                'object' === typeof this.inputTemplates &&
                this.inputTemplates instanceof Array &&
                this.inputTemplates.length > 0
            ) {
                const template = this.inputTemplates.find((template) => this.selectedState === template.country_code);
                if (template) {
                    return template;
                }
            }
            return null;
        },

        parseLicensePlateInput(confirm) {
            this.licensePlateValid = true;

            const option = this.getSelectedLicensePlateOption();
            const canUpdate = null !== option && 'string' === typeof option.replace_whitespaces_with;
            let value = !confirm ? this.licensePlate : this.licensePlateConfirm;

            if (value.length && canUpdate) {
                value = value.replace(/\s+/g, option.replace_whitespaces_with);
                if (confirm) {
                    this.licensePlateConfirm = value;
                } else {
                    this.licensePlate = value;
                }
            }
        },

        handleLicensePlateCheckEmitStatus(value) {
            this.licensePlateCheckStatus = value;
        },

        setDisplayLicensePlateErrorTimer() {
            if (this.timeoutLicensePlateInvalidId >= 0) {
                clearTimeout(this.timeoutLicensePlateInvalidId);
            }

            this.timeoutLicensePlateInvalidId = setTimeout(() => {
                if (this.licensePlateCheckInvalid) {
                    this.licensePlateShowError = true;
                }
            }, 2000);
        },
    },

    mounted() {},

    watch: {
        residenceInAustria() {
            if (!this.expiryOfRightOfWithdrawalActive) {
                this.customerOutsideAustria = true;
                return;
            }

            this.customerOutsideAustria = !(this.residenceInAustria || this.selectedState === 'AT');
        },

        licensePlateCheckInvalid() {
            if (this.licensePlateCheckInvalid === true) {
                this.setDisplayLicensePlateErrorTimer();
            } else {
                this.licensePlateShowError = false;
            }
        },

        licensePlateConfirm() {
            this.setDisplayLicensePlateErrorTimer();
        },
    },

    computed: {
        chassisNumberInputIsRequired() {
            if (this.selectedProducts.length <= 0) {
                return false;
            }

            const isRequired = this.selectedProducts.some((x) => {
                return x.product.requiresChassisNumber === true;
            });

            return isRequired;
        },

        licenseInputTemplate() {
            const licensePlate = this.getSelectedLicensePlate();

            if (licensePlate.length <= 0) {
                return;
            }

            return this.inputTemplates.filter((x) => x.country_code === this.selectedState);
        },

        userData() {
            return {
                licensePlate: this.licensePlate,
                customerOutsideAustria: !this.expiryOfRightOfWithdrawalActive ? true : this.residenceInAustria,
                email: this.email,
                chassisNumberRequired: this.chassisNumberInputIsRequired,
                chassisNumber: this.chassisNumber,
            };
        },

        licensePlateCheckInvalid() {
            return (
                this.licensePlate.trim().length > 0 &&
                this.licensePlateConfirm.trim().length > 0 &&
                (this.licensePlate.localeCompare(this.licensePlateConfirm) !== 0 || !this.licensePlateCheckStatus)
            );
        },

        /**
         * @returns {boolean}
         */
        expiryOfRightOfWithdrawalActive() {
            return this.enableExpiryOfRightOfWithdrawalSetting;
        },
    },
};
</script>
